import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import React from "react";

import { RobotoIconButton } from "@/shared/components";

export interface HiddenTextRevealProps {
  initialValue: string;
  isLoading: boolean;
  onSaveEdit: (newValue: string) => void;
  onShowSecret: () => void;
}

const hiddenText = "••••••••••••••••••••••••••••••";

export const HiddenTextReveal: React.FC<HiddenTextRevealProps> = ({
  initialValue,
  isLoading,
  onSaveEdit,
  onShowSecret,
}) => {
  const theme = useTheme();

  const [isEditing, setIsEditing] = React.useState(false);
  const [showSecret, setShowSecret] = React.useState(false);
  const [secretValue, setSecretValue] = React.useState("");

  React.useEffect(() => {
    setSecretValue(initialValue);
  }, [initialValue]);

  return (
    <TextField
      // disable browser suggestions for passwords
      autoComplete={"false"}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEditing) {
          setSecretValue(event.target.value);
        }
      }}
      onFocus={() => {
        if (showSecret) {
          setIsEditing(true);
        }
      }}
      size="small"
      variant="outlined"
      value={
        (showSecret || isEditing) && initialValue ? secretValue : hiddenText
      }
      sx={{
        width: "100%",
        backgroundColor:
          (showSecret || isEditing) && initialValue
            ? theme.palette.primary.light
            : theme.palette.background.default,
      }}
      type="text" //{formType}
      //ensure this field is not captured by PostHog
      className="ph-no-capture"
      inputProps={{
        //disable browser suggestions for passwords
        autoComplete: "false",
      }}
      InputProps={{
        readOnly: !isEditing && !showSecret,
        endAdornment: (
          <EndAdornment
            isDataAvailable={!!initialValue}
            isEditing={isEditing}
            isLoading={isLoading}
            isShowSecretEnabled={showSecret}
            onCancelEdit={() => {
              setIsEditing(false);
              setSecretValue(initialValue || "");
            }}
            onSaveEdit={() => {
              onSaveEdit(secretValue);
              setIsEditing(false);
              setShowSecret(true);
            }}
            onHideSecret={() => {
              setShowSecret(false);
            }}
            onShowSecret={() => {
              onShowSecret();
              setShowSecret(true);
            }}
          />
        ),
      }}
    />
  );
};

interface EndAdornmentProps {
  isDataAvailable: boolean;
  isEditing: boolean;
  isLoading: boolean;
  isShowSecretEnabled: boolean;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onHideSecret: () => void;
  onShowSecret: () => void;
}

type AdornmentState =
  | "editing"
  | "showSecret"
  | "hideSecret"
  | "loading"
  | "none";

function getAdornmentState({
  isDataAvailable,
  isEditing,
  isLoading,
  isShowSecretEnabled,
}: EndAdornmentProps): AdornmentState {
  if (isLoading) {
    return "loading";
  }
  if (isEditing) {
    return "editing";
  }
  if (isShowSecretEnabled && isDataAvailable) {
    return "hideSecret";
  }
  if (!isShowSecretEnabled) {
    return "showSecret";
  }
  return "showSecret";
}

const EndAdornment: React.FC<EndAdornmentProps> = (props) => {
  const { onCancelEdit, onSaveEdit, onHideSecret, onShowSecret } = props;

  const theme = useTheme();

  const state = getAdornmentState(props);

  const renderAdornment = () => {
    switch (state) {
      case "editing":
        return (
          <>
            <RobotoIconButton
              eventName="SaveSecretClicked"
              onClick={() => void onSaveEdit()}
            >
              <CheckIcon fontSize="small" color="success" />
            </RobotoIconButton>
            <RobotoIconButton
              eventName="CancelEditSecretClicked"
              onClick={onCancelEdit}
            >
              <ClearIcon fontSize="small" color="error" />
            </RobotoIconButton>
          </>
        );

      case "showSecret":
        return (
          <RobotoIconButton
            eventName="ShowSecretClicked"
            onClick={onShowSecret}
          >
            <RemoveRedEyeIcon fontSize="small" />
          </RobotoIconButton>
        );

      case "hideSecret":
        return (
          <RobotoIconButton
            eventName="HideSecretClicked"
            onClick={onHideSecret}
          >
            <VisibilityOffIcon fontSize="small" />
          </RobotoIconButton>
        );

      case "loading":
        return (
          <CircularProgress
            size="1rem"
            sx={{ marginRight: theme.spacing(1) }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <InputAdornment position="end">
      {
        <Box
          sx={{
            // Width must be constant to prevent layout shift
            width: "3rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {renderAdornment()}
        </Box>
      }
    </InputAdornment>
  );
};
